/**
 * The client pre-flight for a document's `files[]` manifest (spec §2.5).
 *
 * A glTF 2.1 scene or asset document may reference four things, and
 * the point of naming the classes is that a refusal can say which one an entry
 * looked like it was trying to be:
 *
 * | Class          | Form                                   |
 * |----------------|----------------------------------------|
 * | Folder file    | relative `assets/<name>.<ext>`          |
 * | Library ref    | `{apiBase}/assets/{guid}/file` (§3.4)  |
 * | Legacy GLB     | own storage, `trace-cms-assets/{guid}.glb` |
 * | Foreign media  | absolute URL, GLB/image/stream (§3.3)   |
 *
 * Everything else is rejected: `blob:`, `builtin:`, any other relative path,
 * an own-storage URL outside the legacy GLB exception, an unsupported external MIME,
 * and — for an asset document — any URL into a scene folder (the closure rule,
 * §3.2).
 *
 * **This is a pre-flight, not the gate.** `ManifestValidator` in TraceBackend
 * runs the same classification on every document write and additionally checks
 * what only the server can: that a folder file's blob exists in *this* folder,
 * and that a library ref's row is live and readable. The two must agree on
 * shape, because a document that passes here and fails there is a save the
 * user cannot complete; every rule below is the client half of a rule stated
 * in `TraceCms.Core/Services/Content/ManifestValidator.cs`.
 *
 * The Unix `file:` landmine (tb#848) is the reason "absolute" is spelled out
 * rather than delegated to `new URL()`: `/trace-content/scenes/x.glb` parses
 * as an absolute `file:` URI with an EMPTY host in some parsers, so a leading
 * slash would walk past every host-based check and land in the relative
 * branch. Absolute here means **http(s) with a non-empty host**, always.
 */

import { isExternalMediaMimeType } from '../utils/AssetTypes';
import { parseAssetFileUrl } from '../content/assetRef';

// =============================================================================
// Types
// =============================================================================

/** What a `files[]` entry is allowed to be (spec §2.5). */
export type ManifestUriClass = 'folderFile' | 'libraryRef' | 'foreignMedia' | 'legacyBlob' | 'rejected';

/** One entry of the manifest, as authored. */
export interface ManifestEntry {
    uri?: string;
    mimeType?: string;
}

/** One refused entry: which index, what it looked like, and why. */
export interface ManifestOffender {
    /** Index into `files[]` — the slot the author is looking at. */
    index: number;
    uri: string | undefined;
    /** The class the entry looked like it was trying to be. */
    class: ManifestUriClass;
    reason: string;
}

/** One accepted entry, for callers that want the classification, not only the verdict. */
export interface ManifestReference {
    index: number;
    uri: string;
    mimeType: string | undefined;
    class: Exclude<ManifestUriClass, 'rejected'>;
    /** Asset id, for a library reference. */
    assetId?: string;
}

/** The knobs the two write paths differ on. */
export interface ManifestValidationOptions {
    /**
     * Public base URL of the API, e.g. `https://api-dev.trace3d.app`. A library
     * reference is only recognised against this origin; with none supplied
     * there is no library-reference class at all and such a URI falls through
     * to the foreign-media rules, where a document mime is refused.
     * Fail-closed by construction — the same posture the server takes.
     */
    apiBaseUrl?: string;
    /**
     * Base URL of our blob storage, e.g.
     * `https://tracecmsstorage.blob.core.windows.net`. A URL on this host is
     * refused except for canonical legacy `trace-cms-assets/{guid}.glb`
     * references, which retained manifests protect from cleanup (tb#1015).
     * Folder-owned storage still uses relative paths or the library API.
     */
    storageBaseUrl?: string;
    /**
     * The closure rule (spec §3.2): an asset folder never references a scene
     * folder. On for asset exports, off for scene documents — a scene
     * referencing its own folder's files is the normal case, and the relative
     * form already scopes it.
     *
     * A separate switch rather than a consequence of `storageBaseUrl` because
     * it has to hold whatever host serves the bytes: a CDN in front of the
     * blob endpoint would present a scene folder under a name no host check
     * recognises, and the closure rule must not quietly stop applying then.
     */
    rejectSceneFolderUrls?: boolean;
}

/** The verdict, with every offender rather than the first. */
export interface ManifestValidationResult {
    ok: boolean;
    offenders: ManifestOffender[];
    references: ManifestReference[];
}

// =============================================================================
// Constants
// =============================================================================

/** The subdirectory that holds a folder's content-addressed files (spec §2.1). */
export const FOLDER_FILES_DIRECTORY = 'assets';
/** The prefix a folder file's URI must start with. */
export const FOLDER_FILE_PREFIX = `${FOLDER_FILES_DIRECTORY}/`;
/** The path a library reference has, after the API origin. */
const SCENE_FOLDER_MARKER = 'trace-content/scenes/';

/**
 * Re-exported, not reimplemented (spec §3.4).
 *
 * There was a second, private matcher here until WP-F1: WP-B2 needed one
 * before WP-B3's `src/content/assetRef.ts` existed. Two matchers for one
 * convention is one matcher too many — the pre-flight would refuse a reference
 * the editor happily offers "open asset" for, or the reverse — so this module
 * now classifies with that one and re-exports it under the name every consumer
 * already imports.
 */
export { parseAssetFileUrl } from '../content/assetRef';

// =============================================================================
// Classification
// =============================================================================

/**
 * Classify one manifest entry. Shape only — the existence and readability
 * checks belong to the server (see the module comment).
 */
export function classifyManifestUri(
    uri: string | undefined,
    mimeType: string | undefined,
    options: ManifestValidationOptions = {},
): { class: ManifestUriClass; reason?: string; assetId?: string } {
    if (!uri || !uri.trim()) {
        return { class: 'rejected', reason: 'A files[] entry must carry a uri.' };
    }
    const candidate = uri.trim();

    // Checked first and on the raw string, so the closure rule holds whatever
    // host serves the bytes — including a CDN this code does not recognise.
    if (options.rejectSceneFolderUrls && namesSceneFolder(candidate)) {
        return {
            class: 'rejected',
            reason: 'An asset may not reference a scene folder; promotion copies the bytes across that line instead.',
        };
    }

    const absolute = parseAbsoluteHttpUrl(candidate);
    if (!absolute) {
        return classifyRelative(candidate);
    }

    if (isOwnStorageUrl(absolute, options.storageBaseUrl)) {
        const essence = mimeType?.split(';')[0].trim().toLowerCase();
        if (essence === 'model/gltf-binary'
            && /^https?:\/\/[^/?#]+(\/[^?#]*)/i.exec(candidate)?.[1] === absolute.pathname
            && isLegacyGlbPath(absolute.pathname)) {
            return { class: 'legacyBlob' };
        }
        return {
            class: 'rejected',
            reason: 'A URL into Trace blob storage must be a legacy GUID GLB reference; otherwise use a relative assets/ uri or the asset file endpoint.',
        };
    }

    // The library-reference rule is `src/content/assetRef.ts`'s, not a second
    // copy of it: the editor's "open asset" and this gate must never disagree
    // about what a reference is. With no API base there is no
    // library-reference class at all and the URI falls through to the
    // foreign-media rules, where a document mime is refused — fail-closed by
    // construction, the same posture the server takes.
    const assetId = options.apiBaseUrl
        ? parseAssetFileUrl(absolute.toString(), options.apiBaseUrl)
        : null;
    if (assetId) {
        return { class: 'libraryRef', assetId };
    }

    if (!isExternalMediaMimeType(mimeType)) {
        return {
            class: 'foreignMedia',
            reason: `A URL on a foreign host is only allowed for GLB, image or streamable media; '${mimeType ?? '(no mimeType)'}' is not.`,
        };
    }

    return { class: 'foreignMedia' };
}

/** Validate a whole manifest, reporting every offender with its index and class. */
export function validateManifest(
    entries: ReadonlyArray<ManifestEntry>,
    options: ManifestValidationOptions = {},
): ManifestValidationResult {
    const offenders: ManifestOffender[] = [];
    const references: ManifestReference[] = [];

    entries.forEach((entry, index) => {
        const outcome = classifyManifestUri(entry?.uri, entry?.mimeType, options);
        if (outcome.reason) {
            offenders.push({ index, uri: entry?.uri, class: outcome.class, reason: outcome.reason });
            return;
        }
        references.push({
            index,
            uri: (entry.uri as string).trim(),
            mimeType: entry.mimeType,
            class: outcome.class as Exclude<ManifestUriClass, 'rejected'>,
            ...(outcome.assetId ? { assetId: outcome.assetId } : {}),
        });
    });

    return { ok: offenders.length === 0, offenders, references };
}

/** One message naming every offending index and class. */
export function formatManifestOffenders(offenders: ReadonlyArray<ManifestOffender>): string {
    return offenders
        .map(o => `  • files[${o.index}] (${o.class}): ${o.reason}${o.uri ? ` — '${o.uri}'` : ''}`)
        .join('\n');
}

// =============================================================================
// Predicates — the shape rules, each mirroring one server rule
// =============================================================================

/**
 * Absolute means **http(s) with a non-empty host**, never merely "parses".
 * See the module comment: a leading slash is not absolute (tb#848).
 */
export function parseAbsoluteHttpUrl(value: string): URL | undefined {
    if (!/^https?:\/\//i.test(value)) return undefined;
    try {
        const url = new URL(value);
        return url.host ? url : undefined;
    } catch {
        return undefined;
    }
}

/** Whether a value is an absolute http(s) URL with a host. */
export function isAbsoluteHttpUrl(value: string | undefined): boolean {
    return !!value && parseAbsoluteHttpUrl(value) !== undefined;
}

/**
 * `assets/<name>.<ext>`, one segment deep, no traversal, no leading slash.
 *
 * The name is not pattern-matched against a hash: on the server the
 * blob-existence test is the real gate, and only the server ever writes into a
 * folder, so a name that exists is a name the server minted. Shape is checked
 * because it is what "outside `assets/`" means — and it is what refuses `../`,
 * a nested path, and a bare filename.
 */
export function isFolderFileUri(candidate: string): boolean {
    if (!isAssetsEntry(candidate)) return false;
    const name = candidate.slice(FOLDER_FILE_PREFIX.length);
    if (name.includes('?') || name.includes('#')) return false;
    const dot = name.lastIndexOf('.');
    return dot > 0 && dot < name.length - 1;
}

/**
 * One entry directly under `assets/`: no nesting, no traversal. The shape
 * both a hosted folder file ({@link isFolderFileUri}) and a `.glz` entry
 * share; a bundle's entries carry human names (`nameBundleEntries`), which
 * may hold `#` or `?`, so only the hosted rule refuses those.
 */
export function isAssetsEntry(candidate: string): boolean {
    if (!candidate.startsWith(FOLDER_FILE_PREFIX)) return false;
    const name = candidate.slice(FOLDER_FILE_PREFIX.length);
    return name.length > 0 && !name.includes('/') && !name.includes('\\') && !name.includes('..');
}

// =============================================================================
// Internals
// =============================================================================

function classifyRelative(candidate: string): { class: ManifestUriClass; reason?: string } {
    if (!isFolderFileUri(candidate)) {
        return {
            class: 'rejected',
            reason: `A relative uri must be '${FOLDER_FILE_PREFIX}<name>.<ext>' inside this folder.`,
        };
    }
    return { class: 'folderFile' };
}

/** A path under `trace-content/scenes/`, wherever it appears in the URI. */
function namesSceneFolder(candidate: string): boolean {
    return candidate.toLowerCase().includes(SCENE_FOLDER_MARKER);
}

function isOwnStorageUrl(url: URL, storageBaseUrl: string | undefined): boolean {
    const base = storageBaseUrl ? parseAbsoluteHttpUrl(storageBaseUrl) : undefined;
    return !!base && url.hostname.toLowerCase() === base.hostname.toLowerCase();
}

/** Shipped flat GLB names only; mirrors TraceBackend LegacyBlobReference.TryParse. */
function isLegacyGlbPath(path: string): boolean {
    return /^\/trace-cms-assets\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\.[gG][lL][bB]$/.test(path);
}
